import React, { useMemo, useState, useContext, useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { Button } from "react-bootstrap";
import Screen from '../components/Screen';
import { getCDNImage } from '../globals';
import { calculateDaysUntil, calculateHoursUntil, currencyFormat, i18nValue } from '../functions';
import { SmartLock as SmartLockIcon } from "../components/Icons";
import { Player } from '@lottiefiles/react-lottie-player';
import ReactSwipeButton from 'react-swipe-button';
import { HotelContext } from "../context/hotel";
import { UserContext } from "../context/user";
import { useSmartLocks } from "../hooks/useSmartLocks";
import { T, TF } from "react-translator-component";
import { CheckInContext } from "../context/checkin";
import processHtml from '../components/processHtml';
import useCheckInActions from "../hooks/useCheckInActions";

import { isHostz, isHostzArosa, isHostzBecozy, isHostzBlackSummit, isHostzCozy, isHostzMyde, isHostzSanCarlo, isHostzSteiners, isHostzSwissstay, isSimplex, isHostzSigma, isKalmStay } from "../components/CheckIn/hotelConditions";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faConciergeBell } from "@fortawesome/free-solid-svg-icons";
import ButtonVideoModal from '../components/CheckIn/ButtonVideoModal';
import { CHECKIN_EVENT_TOPICS_REFRESH_ACCESS } from '../constants/checkinEventTopics';

const dataTestMode = null;
// const dataTestMode = {
//   state: "InStay",

//   // TEST: Before
//   // dateStart: "2025-10-01", dateEnd: "2025-10-30",
//   // TEST: After
//   // dateStart = "2025-03-02"; dateEnd = "2025-03-06";
//   // TEST: Day Checkin
//   // dateStart: "2025-06-25", dateEnd: "2025-06-29",
//   // TEST: In Stay
//   dateStart: "2025-07-01", dateEnd: "2025-12-31",
//   webCheckInDone: {
//     value: "2025-05-15" // value: null //
//   }
// }


const SmartLockWithAccess = (props) => {
  const { data: hotel, currency, placeholders, languages } = useContext(HotelContext);
  const { smartLockPinCode, hasSmartLocks, Room, language, RoomCategory } = useContext(UserContext);

  const { data: checkIn, smartLockUnlock, webCheckInDone, refreshStatus, eventVersion, lastEvent } = useContext(CheckInContext);
  // Usar status del check-in actual (no UserContext.inStay): ese flag es por hotel y puede quedar
  // de otra reserva identificada en el mismo browser.
  const inStay = checkIn?.status === "in_stay";
  const [smartLockId, setSmartLockId] = useState(null);
  const [smartLockName, setSmartLockName] = useState(null);
  // const [ smartLocks, setSmartLocks ] = useState(null);
  const [opening, setOpening] = useState(false);
  const [opened, setOpened] = useState(null);
  const [unlockError, setUnlockError] = useState(null);
  const [onlyOneSmartLock, setOnlyOneSmartLock] = useState(false);
  const { getByIds, items: smartLocksAll, unlock, clear: clearSmartLocksCache } = useSmartLocks();
  const swipeRef = useRef(null);

  const { getAccount } = useCheckInActions();

  const [account, setAccount] = useState(null);
  const [isPendingDepositGuarantee, setIsPendingDepositGuarantee] = useState(false);

  const [testMode, setTestMode] = useState(dataTestMode);

  const [loading, setLoading] = useState(true);

  let accessInfoUrl = false
  if (hotel?.extra?.["access-info-video"]?.[language]) {
    accessInfoUrl = hotel?.extra?.["access-info-video"]?.[language]
  }
  // const smartLockIdsDB = Room?.smartLockIds;

  useEffect(() => {
    if (!hotel?.id || !checkIn?.id) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setAccount(null);

    (async () => {
      let statusAfterRefresh = checkIn?.status;
      try {
        const r = await refreshStatus();
        if (r?.status) statusAfterRefresh = r.status;
      } catch (e) {
        console.error('SmartLockWithAccess refreshStatus', e);
      }
      if (cancelled) return;
      clearSmartLocksCache?.();
      try {
        const res = await getAccount(hotel.id, checkIn.id);
        if (cancelled) return;
        if (statusAfterRefresh === "in_stay" && res?.showAccessInfoForced != "hide") {
          if (res) {
            res.showAccessInfo = "show";
          }
        }
        setAccount(res);
        if (res?.depositGuarantee?.required && !res?.depositGuarantee?.paid) {
          setIsPendingDepositGuarantee(true);
        } else {
          setIsPendingDepositGuarantee(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // refreshStatus/getAccount/clear cambian de identidad entre renders; solo re-ejecutar al cambiar hotel o check-in.
  }, [hotel?.id, checkIn?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastSyncedEventVersionRef = useRef(null);

  useEffect(() => {
    lastSyncedEventVersionRef.current = null;
  }, [checkIn?.id]);

  /**
   * EventMark en tiempo real (misma suscripción que CheckInContext): al publicar desde PMS/BO
   * createEventMark({ entity: "checkin", entityId: <uuid check-in>, topic: "reassigned-room" })
   * el contexto ya llama refreshStatus; aquí limpiamos caché de cerraduras y volvemos a cargar cuenta de acceso.
   */
  useEffect(() => {
    if (!hotel?.id || !checkIn?.id) return;
    if (lastSyncedEventVersionRef.current === null) {
      lastSyncedEventVersionRef.current = eventVersion;
      return;
    }
    if (lastSyncedEventVersionRef.current === eventVersion) return;
    lastSyncedEventVersionRef.current = eventVersion;

    const topic = lastEvent?.topic;
    if (topic && !CHECKIN_EVENT_TOPICS_REFRESH_ACCESS.has(topic)) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        clearSmartLocksCache?.();
        const r = await refreshStatus();
        if (cancelled) return;
        const statusAfter = r?.status ?? checkIn?.status;
        const res = await getAccount(hotel.id, checkIn.id);
        if (cancelled) return;
        if (statusAfter === 'in_stay' && res?.showAccessInfoForced != 'hide' && res) {
          res.showAccessInfo = 'show';
        }
        setAccount(res);
        if (res?.depositGuarantee?.required && !res?.depositGuarantee?.paid) {
          setIsPendingDepositGuarantee(true);
        } else {
          setIsPendingDepositGuarantee(false);
        }
      } catch (e) {
        console.error('SmartLockWithAccess realtime access refresh', e);
      }
    })();
    return () => { cancelled = true; };
  }, [eventVersion, lastEvent?.topic, hotel?.id, checkIn?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const smartLocks = useMemo(() => {
    if (!smartLocksAll || smartLocksAll.lenght === 0) {
      return false;
    }
    const _r = smartLocksAll.filter(s => s.allowUnlock)
    // let r = getByIds(smartLockIdsDB);
    // // PARCHE -----
    // if (!r) {
    //   setTimeout(() => {
    //     console.log('2609 - TO - smartLockIdsDB', smartLockIdsDB)
    //     r = getByIds(smartLockIdsDB);
    //   }, 2000);
    // }
    // // PARCHE
    return _r;
    // -------
  }, [smartLocksAll])

  const formatoHoraAmPm = (fechaStr) => {
    const fecha = new Date(fechaStr.replace(' ', 'T')); // Para que sea válido en todos los navegadores
    let horas = fecha.getHours();
    const ampm = horas >= 12 ? 'pm' : 'am';
    horas = horas % 12;
    horas = horas ? horas : 12; // el 0 se convierte en 12
    return `${horas} ${ampm}`;
  }

  const normalizeHour = (input, format = "24") => {
    const original = input.trim();

    // Si ya está en HH:mm y se pide formato 24h, devolver directamente
    if (format === "24" && /^\d{1,2}:\d{2}$/.test(original)) {
      return original;
    }

    // Limpiamos el input: sin puntos, espacios duplicados, lowercase
    const cleaned = original
      .replace(/\./g, '')         // quitamos puntos
      .replace(/\s+/g, ' ')       // unificamos espacios
      .trim()
      .toLowerCase();

    // Regex para formato 12h con o sin minutos
    const regex = /^(\d{1,2})(?::(\d{2}))?\s?(am|pm|a m|p m)$/i;
    const match = cleaned.match(regex);

    // Si es HH:mm, lo parseamos como 24h
    if (/^\d{1,2}:\d{2}$/.test(cleaned)) {
      const [hh, mm] = cleaned.split(':').map(Number);
      if (format === "24") {
        return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
      } else {
        const period = hh >= 12 ? 'pm' : 'am';
        const hour12 = hh % 12 === 0 ? 12 : hh % 12;
        return `${hour12}:${mm.toString().padStart(2, '0')} ${period}`;
      }
    }

    if (!match) return null;

    let [, hour, minutes = '00', period] = match;

    hour = parseInt(hour, 10);
    minutes = parseInt(minutes, 10);

    if (period.includes('p') && hour < 12) hour += 12;
    if (period.includes('a') && hour === 12) hour = 0;

    const hh = hour.toString().padStart(2, '0');
    const mm = minutes.toString().padStart(2, '0');

    if (format === "24") {
      return `${hh}:${mm}`;
    } else {
      const periodOut = hour >= 12 ? 'pm' : 'am';
      const hour12 = hour % 12 === 0 ? 12 : hour % 12;
      return `${hour12}:${mm} ${periodOut}`;
    }
  }

  // const smartLocks = useMemo(() => {
  //   console.log('2609 - smartLockIdsDB', smartLockIdsDB)
  //   if (!smartLockIdsDB || smartLockIdsDB.lenght === 0) {
  //     return false;
  //   }

  //   let sortedList = [];
  //   let r = getByIds(smartLockIdsDB);
  //   if (r) {
  //     sortedList = [...r];
  //     // sortedList = r.sort((a, b) => {
  //     //   const aIsMain = a.name?.toUpperCase().includes("MAIN");
  //     //   const bIsMain = b.name?.toUpperCase().includes("MAIN");
  //     //   return bIsMain - aIsMain;
  //     // });
  //   } else {

  //     // PARCHE -----
  //     setTimeout(() => {
  //       console.log('2609 - TO - smartLockIdsDB', smartLockIdsDB)
  //       r = getByIds(smartLockIdsDB);
  //       if (r) {
  //         sortedList = [...r];
  //         // sortedList = r.sort((a, b) => {
  //         //   const aIsMain = a.name?.toUpperCase().includes("MAIN");
  //         //   const bIsMain = b.name?.toUpperCase().includes("MAIN");
  //         //   return bIsMain - aIsMain;
  //         // });
  //       }
  //     }, 2000);
  //     // PARCHE

  //   }

  //   return sortedList;
  //   // -------
  // }, [smartLockIdsDB, smartLocksItemsGeneral])

  useEffect(() => {
    if (smartLocks?.length == 1) {
      setOnlyOneSmartLock(true);
      setSmartLockId(smartLocks[0].id);
      setSmartLockName(smartLocks[0].name);
    }

  }, [smartLocks]);

  const roomCode = useMemo(() => {
    if (dataTestMode) {
      return "hab 123";
    }

    return (Room?.code || checkIn?.reservation?.unit?.name);
  }, [Room, checkIn])

  const roomFloorData = useMemo(() => {
    if (dataTestMode) {
      return "piso 3";
    }
    if (checkIn?.Room?.floor) {
      return checkIn.Room.floor;
    }
    return "";
  }, [checkIn]);

  // useEffect(() => {
  //   console.log('2609 - smartLocksItemsGeneral', smartLocksItemsGeneral)
  //   let r = getByIds(smartLockIdsDB);
  //     if (r) {
  //       setSmartLocks(r)
  //     } else {
  //       setTimeout(() => {
  //         console.log('2609 - TO - smartLockIdsDB', smartLockIdsDB)
  //         r = getByIds(smartLockIdsDB);
  //         setSmartLocks(r)
  //       }, 5000);
  //     }
  // }, [ smartLockIdsDB, smartLocksItemsGeneral ])

  // KalmStay: PIN de la cerradura del check-in (llega al identificar) o el de la estadía.
  const kalmStayPinCode = checkIn?.smartLockPinCode || smartLockPinCode || null;

  const {
    showSmartLockSection,

    titleSlidersSection,

    hasMainSection,
    titleMainSection,
    descriptionMainSection,

    hasKeySection,
    titleKeySection,
    descriptionKeySection,

    showCoverSliders,
    showCoverMain,
    showTextMain,
    showCoverKeys,
    showCoverBefore,
    showCoverAfter,
    showExtraMsg,
    showDataInStay } = useMemo(() => {

      let res = {
        showSmartLockSection: hasSmartLocks,
        titleSlidersSection: T("status.access_code.main_entrance"),
        hasMainSection: false,
        titleMainSection: '',
        descriptionMainSection: '',
        hasKeySection: false,
        titleKeySection: '',
        descriptionKeySection: '',
        showCoverSliders: false,
        showCoverMain: null,
        showTextMain: null,
        showCoverKeys: true,
        showCoverBefore: null,
        showCoverAfter: null,

        showExtraMsg: null,

        showDataInStay: false,
        webCheckInDone: webCheckInDone,
        showAccessInfo: account?.showAccessInfo ? account?.showAccessInfo : "hide",
        showAccessInfoForced: account?.showAccessInfoForced
      }

      const now = new Date();
      let dateStart = checkIn.dateStart;
      let dateEnd = checkIn.dateEnd;
      let hoursStart = null;
      let hoursEnd = null;
      let hoursCheckinDayGeneral = null;
      let hoursCheckinDayKeys = null;

      let customCoverBefore = null;


      if (testMode?.dateStart) { dateStart = testMode.dateStart }
      if (testMode?.dateEnd) { dateEnd = testMode.dateEnd }

      // Para todo Hostz => no mostrar
      if (isHostz(hotel.id)) {
        // res.showSmartLockSection = false;
      }

      // TODO_G: Los montos de SD y horario de checkin traerlos dinámicamente (cuande esté hecho en el back)
      let sd = 0;

      if (isSimplex(hotel.id)) {
        res.hasKeySection = true;
        res.titleKeySection = T("status.room_keys");

        hoursCheckinDayGeneral = `${dateStart} 14:00:00`; // 2 pm
        hoursCheckinDayKeys = `${dateStart} 14:00:00`;  // 2 pm

        res.titleKeySection = T("status.apartment_access");
        res.descriptionKeySection = ''

      }

      if (isHostz(hotel.id)) {
        res.hasKeySection = true;
        res.titleKeySection = T("status.room_keys");
        res.descriptionKeySection = T('status.room_keys_description');

        // TODO_G: Implementar desde hotel.extra.checkin-hour y time-zone lo de los horarios (Como está Cozy)

        //--- Custom por hotel -->
        
        if (isHostzSigma(hotel.id)) {
          hoursCheckinDayGeneral = `${dateStart} 10:00:00`; // 10 am
          hoursCheckinDayKeys = `${dateStart} 16:00:00`;  // 4 pm
          // sd = 100;
          res.hasMainSection = true;
          res.titleMainSection = T("status.access_information.access_to_hotel");
          res.descriptionMainSection = '';
  
          res.titleKeySection = T("status.access_information.access_to_room");
          res.descriptionKeySection = '';
          res.showSmartLockSection = false;
        }


        if (isHostzSwissstay(hotel.id)) {
          res.titleKeySection = T("status.apartment_keys");
          res.descriptionKeySection = T('status.apartment_keys_description');

          hoursCheckinDayGeneral = `${dateStart} 10:00:00`; // 10 am, igual que Sigma
          hoursCheckinDayKeys = `${dateStart} 16:00:00`;  // 4 pm, igual que Sigma
          sd = 150;

          // Solo si la room tiene smartlock: mostrar bloque de PIN (como Sigma)
          if (hasSmartLocks) {
            res.hasMainSection = true;
            res.titleMainSection = T("status.access_information.accesses_swissstay");
            res.descriptionMainSection = '';
            res.showSmartLockSection = false;
          }
        }

        if (isHostzSteiners(hotel.id)) {

          hoursCheckinDayGeneral = `${dateStart} 15:00:00`; // 3pm
          hoursCheckinDayKeys = `${dateStart} 15:00:00`;  // Igual que General (muestra la info el día de checkin a la hora indicada)

          res.hasMainSection = true;
          res.titleMainSection = T("status.access_information.main_entrance");
          res.descriptionMainSection = T("status.access_information.main_entrance_description_steiners");

          res.hasKeySection = true;
          res.titleKeySection = T("status.access_information.access_to_room");
          res.descriptionKeySection = T("status.access_information.access_to_room_description_steiners");
          res.showExtraMsg = '';
          // res.showExtraMsg = TF('status.access_information_extramsg_steiners', formatoHoraAmPm("2000-01-01 15:00:00"), formatoHoraAmPm("2000-01-01 21:00:00"));

          sd = 150;

          res.showSmartLockSection = false;
        }
        //<-- Custom por hotel ---        
      }

      // 2025-05-08: Todas las tapas before tienen el mismo cartel:
      // Access information will be visible on the check in day at [[CHECKIN_TIME]], only if the check-in form has been completed and the payment has been settled (included the security deposit of [[DEPOSIT_AMOUNT]] [[CURRENCY]] ).
      if (hoursCheckinDayGeneral && hoursCheckinDayKeys) {
        if (sd > 0) {
          sd = currencyFormat(sd, currency);
          let customCoverBeforeTranlationCode = 'status.access_information.before_checkin_with_hours_cozy'
          if (isHostzSteiners(hotel?.id)) {
            customCoverBeforeTranlationCode = 'status.access_information.before_checkin_with_hours_steiners';
          }
          customCoverBefore = TF(customCoverBeforeTranlationCode, formatoHoraAmPm(hoursCheckinDayGeneral), sd);
        } else {
          if (hoursCheckinDayGeneral == hoursCheckinDayKeys) {
            customCoverBefore = TF('status.access_information.before_checkin_with_hours_no_sd', formatoHoraAmPm(hoursCheckinDayGeneral));
          }
        }
      }


      let keyT = '';
      // Días de estadía
      // console.log('HORARIOS:: CHECKIN', dateStart);
      // console.log('HORARIOS:: días antes de checkin', calculateDaysUntil(dateStart));
      // console.log('HORARIOS:: CHECKOUT', dateEnd);
      // console.log('HORARIOS:: días hasta el checkout', calculateDaysUntil(dateEnd));

      // Cover After => DESPUÉS DEL CHECKOUT
      if (calculateDaysUntil(dateEnd) < 0) {
        keyT = `status.access_information.after_checkin_${hotel.code}`;
        res.showCoverAfter = T(keyT);
        if (res.showCoverAfter == keyT) {
          res.showCoverAfter = T("status.access_information.after_checkin");
        }
      } else {
        // Cover Before => ANTES DEL CHECKIN
        if (calculateDaysUntil(dateStart) > 0) {
          if (hoursCheckinDayGeneral && hoursCheckinDayKeys) {
            if (customCoverBefore) {
              res.showCoverBefore = customCoverBefore;
            } else {
              res.showCoverBefore = TF('status.access_information.before_checkin_with_hours', formatoHoraAmPm(hoursCheckinDayGeneral), formatoHoraAmPm(hoursCheckinDayKeys));
            }

          } else {
            res.showCoverBefore = T('status.access_information.before_checkin');
          }

        }
      }

      if (testMode?.state) {
        res.showDataInStay = testMode.state == "InStay";
      } else {
        res.showDataInStay = inStay;
      }

      if (res.showDataInStay) {

        // Si estas en estadía => muestro todo
        res.showCoverKeys = false;
        res.showCoverBefore = null; // No muestro tapa antes de checkin

      } else {
        if (!res.showCoverBefore && !res.showCoverAfter) {

          // Si hay horarios configurados y no está en inStay => mostrar COVER segun configuración

          if (!hoursCheckinDayGeneral || !hoursCheckinDayKeys) {

            res.showCoverKeys = false;

          } else {

            // res.hasKeySection = false;

            // console.log('HORARIOS:: Umbral General::', hoursCheckinDayGeneral);
            // console.log('HORARIOS:: horas hasta el umbral general::', calculateHoursUntil(hoursCheckinDayGeneral));
            if (calculateHoursUntil(hoursCheckinDayGeneral) > 0.25) { // 15 minutos antes del umbral

              // Muestro Info antes de horario general
              // if (isHostzMyde(hotel.id)) {
              //   res.showCoverMain = TF('status.access_information.before_checkin_with_hours_themyde', formatoHoraAmPm(hoursCheckinDayGeneral));
              // } else {
              //   res.showCoverMain = TF('status.access_information.before_checkin_with_hours', formatoHoraAmPm(hoursCheckinDayGeneral), formatoHoraAmPm(hoursCheckinDayKeys));
              // }
              if (customCoverBefore) {
                res.showCoverBefore = customCoverBefore;
              } else {
                res.showCoverBefore = TF('status.access_information.before_checkin_with_hours', formatoHoraAmPm(hoursCheckinDayGeneral), formatoHoraAmPm(hoursCheckinDayKeys));
              }
              // No muestro la sección de keys
              res.hasKeySection = false;

            } else {
              if (calculateHoursUntil(hoursCheckinDayKeys) > 0.25) {
                // Muestro Info horario general

                // res.showTextMain = TF('status.access_information.before_checkin_main', formatoHoraAmPm(hoursCheckinDayGeneral), formatoHoraAmPm(hoursCheckinDayKeys));
                res.showCoverKeys = T("status.access_information.access_to_room_cover");
                if (isHostzArosa(hotel?.id)) {
                  res.showCoverKeys = T("status.access_information.access_to_room_cover_arosa");
                } else if (isHostzSigma(hotel?.id)) {
                  res.showCoverKeys = T("status.access_information.access_to_room_cover_sigma");
                }

              } else {
                // Muestro Info completa
                res.showCoverKeys = false;
                if (isHostzSigma(hotel?.id) && !inStay) {
                  res.showCoverKeys = true;
                }
              }
            }
          }
          // if (checkIn?.inStay || (hours <= 24)) {
          //   textValue = value['in-period'][lang];
          //   console.log('5feb - textValue', textValue);
          //   if (!textValue) {
          //     textValue = value['in-period']['en-US'];
          //   }
          // }
        }
      }

      // CONDICIONES MÁS IMPORTANTES (SI NO ES IN-STAY): 

      if (!res.showDataInStay) {
        // Si no hay tapas visibles => evalúo
        // 1. muestro tapa si no está el webCheckin hecho
        // 2. muestro tapa si el SD no fue realizado
        // 3. muestro tapa si el balance no es cero (Pendiente de hacer)
        if (testMode?.webCheckInDone) {
          res.webCheckInDone = testMode.webCheckInDone.value;
        }
        if (!res.showCoverBefore && !res.showCoverAfter) {
          if (!res.webCheckInDone) {
            // --- 1. WEB CHECKIN ---
            if (customCoverBefore) {
              res.showCoverBefore = customCoverBefore;
            } else {
              res.showCoverBefore = T('status.access_information.before_checkin');
            }
            // --- 1. WEB CHECKIN ---
          } else if (isPendingDepositGuarantee) {
            // --- 2. DEBE SD ---
            if (customCoverBefore) {
              res.showCoverBefore = customCoverBefore;
            } else {
              res.showCoverBefore = T('status.access_information.before_checkin');
              // Podemos incluir el link a pagar
            }
            // --- 2. DEBE SD ---
          }
        }
      }

      if (res.showAccessInfoForced == "show" || inStay) {
        // MUESTRO TODO
        res.showCoverKeys = false;
        res.showCoverBefore = null;
        res.hasKeySection = true;
      }

      if (res.showAccessInfo == "hide") {
        // OCULTO TODO
        if (customCoverBefore) {
          res.showCoverBefore = customCoverBefore;
        } else {
          res.showCoverBefore = T('status.access_information.before_checkin');
        }
      }

      if (isKalmStay(hotel.id)) {
        // KalmStay: sin círculo de PIN, sin lista de cerraduras ni swipe; una sola tarjeta de habitación
        // (PIN + número + piso) y nunca una tarjeta vacía.
        res.showSmartLockSection = false;
        res.hasKeySection = !!(kalmStayPinCode || roomCode);
        res.titleKeySection = T("status.access_information.access_to_room");
        res.descriptionKeySection = '';
      }

      return res;
    }, [hotel, checkIn, inStay, isPendingDepositGuarantee, account, hasSmartLocks, testMode, kalmStayPinCode, roomCode]);

  const SmartLockItem = ({ data, selected }) => {

    let name = data.nameI18n ? i18nValue(data, "name", language) : "";
    if (!name) {
      name = data.name ?? data.externalName;
    }
    // console.log('sortedList nameI18n', name);

    return (<li style={{ color: !selected ? "#666" : '#FFF', background: !selected ? "#FFF" : '#70a2d9', paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12, border: "2px solid #DDD", borderColor: !selected ? '#DDD' : '#70a2d9', margin: 3, display: "inline-block", borderRadius: 8, cursor: "pointer", fontSize: 14 }} onClick={() => {
      if (selected) {
        setSmartLockId(null)
        setSmartLockName(null)
      } else {
        setSmartLockId(data.id)
        setSmartLockName(data.name)
        setUnlockError(null)
      }
    }}>
      {
        <span>{name}</span>
      }
    </li>)
  }

  useEffect(() => {
    if (swipeRef?.current) {
      swipeRef.current.classList.remove('fade-enter-active');
      setOpening(false);
      setOpened(null);
      if (smartLockId) {
        setTimeout(() => {
          swipeRef.current.classList.add('fade-enter-active');
        }, 200);
      }
    }
  }, [swipeRef, smartLockId])


  function keysToUpperCase(obj) {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;

    const result = {};

    for (const key in obj) {
      const upperKey = key.toUpperCase();
      const value = obj[key];

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result[upperKey] = {};
        for (const subkey in value) {
          result[upperKey][subkey.toUpperCase()] = value[subkey];
        }
      } else {
        result[upperKey] = value;
      }
    }

    return result;
  }

  const getPlaceholderValueFromCheckin = (phKey) => {
    const placeholders = { ...checkIn?.placeholders }
    let ph_value = '--'
    
    if (placeholders) {
      if (placeholders?.[phKey]) {
        const ph_values = placeholders[phKey];
        if (ph_values?.[language]) {
          ph_value = ph_values[language];
        } else {
          if (ph_values?.['en-US']) {
            ph_value = ph_values['en-US'];
          }
        }
      }
    }
    return ph_value
  }


  const loadFromPlaceholder = (key, subkey, lang, placeholders = null) => {

    const arrPlaceHolders = keysToUpperCase({ ...placeholders });
    
    let value = null;
    let textValue = '--';
    if (arrPlaceHolders) {
      value = arrPlaceHolders[key];
      if (value) {
        if (subkey) {
          if (value[subkey]) {
            if (value[subkey][lang]) {
              textValue = value[subkey][lang];
            } else {
              if (value[subkey]['en-US']) {
                textValue = value[subkey]['en-US'];
              }
            }
          }
        }
      }
    }
    return textValue;
  }

  const hotelAddress = (data, key, lang, placeholdersParam = null) => {
    let textValue = hotel?.address ?? '--';
    const mergedPlaceholders = { ...placeholdersParam, ...checkIn?.placeholders };
    const unitGroupAddress = mergedPlaceholders?.['UNITGROUP-ADDRESS'];

    if (!unitGroupAddress) {
      return textValue;
    }

    if (typeof unitGroupAddress === 'string') {
      return unitGroupAddress;
    }

    const resolved = unitGroupAddress[lang] || unitGroupAddress[language] || unitGroupAddress['en-US'];
    if (resolved) {
      return resolved;
    }

    return textValue;
  }

  const stripStyles = (html) => {
    // Crear un contenedor temporal
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Eliminar etiquetas <style>
    const styles = tempDiv.querySelectorAll('style');
    styles.forEach((style) => style.remove());

    // Eliminar atributos style
    const elementsWithStyle = tempDiv.querySelectorAll('[style]');
    elementsWithStyle.forEach((el) => el.removeAttribute('style'));

    // Devolver el HTML limpio
    return tempDiv.innerHTML;
  };

  const showRoomData = (isApartment = false, showAddress = null) => {
    const roomAddress = isApartment ? T("apartment.address") : T("room.address");
    const roomNumber = isApartment ? T("apartment.number") : T("room.number");
    const roomFloor = isApartment ? T("apartment.floor") : T("room.floor");
    return (
      <div className="mb-0">
        {showAddress && roomAddress && (
          <>
            <p className="mb-0">{roomAddress}</p>
            <h6 className="font-weight-semibold font-size-md mb-1">
              {processHtml(showAddress)}
            </h6>
          </>
        )}

        {roomCode && (
          <>
            <p className="mb-0">{roomNumber}</p>
            <h6 className="font-weight-semibold font-size-md mb-1">
              {roomCode}
            </h6>
          </>
        )}

        {
          roomFloorData && (
            <>
              <p className="mb-0 mt-2">{roomFloor}</p>
              <h6 className="font-weight-semibold font-size-md mb-1">
                {roomFloorData}
              </h6>
            </>
          )
        }
      </div>
    )
  }

  const isPlaceholderValid = (phKey) => {
    const placeholders = { ...checkIn?.placeholders }
    let isValid = false;
    if (placeholders) {
      if (placeholders?.[phKey]) {
        isValid = true;
      }
    }
    return isValid;
  }



  const showMsgMultipleSmartLock = () => {
    if (isHostzMyde(hotel.id)) {
      return <p className="text-center mb-4">{T('checkin.select-door-open-themyde')}</p>
    }
    if (isHostz(hotel.id)) {
      return <p className="text-center mb-4">{T('checkin.select-door-open-hostz')}</p>
    }
    return "";
  }

  if (loading) {
    return <Screen
      section={`smartlocks`}
      title={String(T("status.access_information")).toUpperCase()}
    >
      <div className="loading text-center mt-5 p-10">
        <div className="spinner-border" role="status"></div>
      </div>
    </Screen>
  }

  const beforeArrivalInstructions = loadFromPlaceholder('CUSTOM-BEFORE-ARRIVAL-INSTRUCTIONS', "__MAIN__", language, placeholders)
  const afterArrivalInstructions = loadFromPlaceholder('CUSTOM-AFTER-ARRIVAL-INSTRUCTIONS', "__MAIN__", language, placeholders)
  /** `loadFromPlaceholder` devuelve `'--'` cuando no hay valor; eso es truthy y no debe tapar el texto por defecto. */
  const beforeArrivalCoverText =
    beforeArrivalInstructions && beforeArrivalInstructions !== '--' ? beforeArrivalInstructions : showCoverBefore
  const afterArrivalCoverText =
    afterArrivalInstructions && afterArrivalInstructions !== '--' ? afterArrivalInstructions : showCoverAfter
  
  if (showCoverBefore || showCoverAfter ) {
    return (
      <Screen
        section="smartlocks"
        title={String(T("status.access_information")).toUpperCase()}
      >
        { /* TEST MODE */}
        {testMode && (
          <div className="" style={{ color: "#F00" }}>TEST: {JSON.stringify(testMode)}</div>
        )}

        <div className="d-flex flex-column justify-content-center align-items-center" style={{ fontSize: "1.25rem", color: "#999", lineHeight: 1.25 }}>
          <FontAwesomeIcon icon={faConciergeBell} size="lg" style={{ fontSize: 140, marginTop: 120, marginBottom: 20 }} />
          {(showCoverAfter || showCoverBefore) && (
            <div className="text-center px-4">
              {showCoverBefore && (
                <div className="text-center px-4">
                  {processHtml(beforeArrivalCoverText)}
                </div>
              )}
              {showCoverAfter && (
                <div className="text-center px-4">
                  {processHtml(afterArrivalCoverText)}
                </div>
              )}
              {showExtraMsg && (
                <div className="mt-3">{showExtraMsg}</div>
              )}
            </div>
          )}
        </div>
      </Screen>
    )
  }
  // const unitAccessIntructions = loadFromPlaceholder('UNIT-ROOM-ACCESS-INSTRUCTIONS-SHORT', (Room?.RoomCategory?.code || RoomCategory?.code) + "--" + Room?.code, language, placeholders)
  // const unitMainEntrance = loadFromPlaceholder('UNIT-MAIN-ENTRANCE', (Room?.RoomCategory?.code || RoomCategory?.code) + "--" + Room?.code, language, placeholders)
  const unitAccessIntructions = getPlaceholderValueFromCheckin('UNIT-ROOM-ACCESS-INSTRUCTIONS-SHORT')
  const unitMainEntrance = getPlaceholderValueFromCheckin('UNIT-MAIN-ENTRANCE')
  const mainEntranceLockbox = loadFromPlaceholder('PROPERTY-MAIN-ENTRANCE-LOCKBOX', "__MAIN__", language, placeholders)
  const unitGroupBackupAccessCode = getPlaceholderValueFromCheckin('UNITGROUP-BACKUP-ACCESS-CODE')
  const unitGroupAccessTitlePh = getPlaceholderValueFromCheckin('UNITGROUP-ACCESS-TITLE')
  const unitGroupEntrance = getPlaceholderValueFromCheckin('UNITGROUP-ENTRANCE')

  const getUnitGroupAccessTitle = (fallbackI18nKey) => {
    if (unitGroupAccessTitlePh && unitGroupAccessTitlePh !== '--') {
      return stripStyles(unitGroupAccessTitlePh);
    }
    return T(fallbackI18nKey);
  };

  return (
    <Screen
      section="smartlocks"
      title={String(T("status.access_information")).toUpperCase()}
    >
      { /* TEST MODE */}
      {testMode && (
        <div className="" style={{ color: "#F00" }}>TEST: {JSON.stringify(testMode)}</div>
      )}

      {showCoverMain && (
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ fontSize: "1.25rem", color: "#999", lineHeight: 1.25 }}>
          <FontAwesomeIcon icon={faConciergeBell} size="lg" style={{ fontSize: 140, marginTop: 120, marginBottom: 20 }} />
          <div className="text-center px-4">{processHtml(showCoverMain)}</div>
        </div>
      )}

      {(!showCoverBefore && !showCoverAfter && !showCoverMain) && (
        <>
          {isSimplex(hotel.id) && (
            <>
              <div className="text-center p-2 mb-2" style={{
                background: "white",
                borderRadius: "8px",
                border: "1px solid #DDD",
                color: "#666"
              }}>
                <div className="d-flex flex-column justify-content-center items-align-center text-center">
                  <p className="mb-0">{T("hotel.address")}</p>
                  <h6 className="font-weight-semibold font-size-md mb-1" dangerouslySetInnerHTML={{ __html: stripStyles(hotelAddress(null, 'desc', language, placeholders)) }}></h6>
                </div>
              </div>
            </>
          )}

        </>
      )}

      {!showCoverMain && (
        <>
          {showSmartLockSection && (
            <>

              { /* LOADING */}
              {!smartLocks && (
                <div className="loading text-center mt-5"><div className="spinner-border" role="status"></div></div>
              )}

              {smartLocks && (
                <div className="text-center p-4 mt-3 mb-4"
                  style={{
                    background: "white",
                    borderRadius: "8px",
                    border: "1px solid #DDD",
                    color: "#666"
                  }}>
                  <h4 className="d-flex justify-content-center align-items-center">
                    {String(titleSlidersSection).toUpperCase()}
                  </h4>

                  {!showCoverSliders && (
                    <>
                      {opened === null && !opening && (
                        <>
                          {smartLockPinCode && (
                            <div className="my-4">
                              <div className="icon-circle d-flex justify-content-center align-items-center" style={{
                                margin: "auto",
                                width: 120,
                                height: 120,
                                borderRadius: '50%',
                                background: '#FFF',
                                boxShadow: '0 10px 20px #CCC'
                              }}>
                                <span className="d-block" style={{ color: '#FFF', background: '#444', borderRadius: 8, marginTop: -30, marginLeft: 40, marginRight: 40, padding: 8 }}>
                                  <span className="font-weight-light" style={{ opacity: 0.5, marginRight: 10 }}>PIN CODE</span>
                                  <span className="font-weight-bold">{smartLockPinCode}</span>
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {!onlyOneSmartLock && (
                        <>
                          {showMsgMultipleSmartLock()}
                        </>
                      )}

                      {(!onlyOneSmartLock && !opening && opened === null) && (
                        <ul style={{ listStyle: "none", margin: 0, padding: 0, textAlign: "center" }}>
                          {smartLocks.map((sm, idx) => {
                            if (sm.partner === "keynest") return;
                            return (<SmartLockItem key={`smartLocks-item-${idx}`} data={sm} selected={sm.id === smartLockId} />)
                          })}
                        </ul>
                      )}

                      {smartLockId && (
                        <div className="text-center" style={{ marginTop: 20, marginLeft: 40, marginRight: 40 }}>
                          {opening && (
                            <Player
                              // ref={this.player} // set the ref to your class instance
                              autoplay={true}
                              loop={true}
                              src="/lottie/loading.json"
                              style={{ height: '500px', width: '200px' }}
                            ></Player>
                          )}

                          {opened === false && <p style={{ height: 500, paddingTop: 200, color: "red" }}>Error!</p>}

                          {opened && (
                            <Player
                              // ref={this.player} // set the ref to your class instance
                              autoplay={true}
                              loop={false}
                              keepLastFrame={true}
                              src="/lottie/success.json"
                              style={{ height: '500px', width: '300px' }}
                            ></Player>
                          )}

                        </div>
                      )}
                    </>
                  )}

                  <div className="px-4"
                    style={{
                      minHeight: smartLockId ? "40px" : "0px",
                      marginBottom: smartLockId ? "10px" : "0px",
                    }}>
                    {!unlockError && <div ref={swipeRef} className="fade-enter">
                      <ReactSwipeButton
                        // text='SWIPE TO OPEN'
                        text={T("smartlocks.swipe-to-open")}
                        text_unlocked={opened ? 'OPEN' : 'OPENING...'}
                        color={opened ? '#0d8e24' : '#0597c6'}
                        onSuccess={() => {
                          setUnlockError(null);
                          setOpening(true);
                          unlock(smartLockId).then(r => {
                            setOpening(false);
                            if (r?.result) {
                              setOpened(true);
                              setTimeout(() => {
                                window.location.reload()
                              }, 3000);
                            } else {
                              setOpened(null);
                              setUnlockError(r?.reason || "Error");
                            }
                          })
                          // smartLockUnlock(smartLockId).then(r => {
                          //   console.log('2709 - rrrrr', r)
                          //   setOpening(false);
                          //   setOpened(r.status === 'Unlocked');
                          //   setTimeout(() => {
                          //     window.location.reload()
                          //   }, 2000);
                          // })
                          // setTimeout(() => {
                          //   setOpening(false);
                          //   setOpened(true)
                          //   setTimeout(() => {
                          //     // setSmartLockId(null);
                          //     // setOpening(false);
                          //     // setOpened(false)
                          //     window.location.reload()
                          //   }, 3000);
                          // }, 2000); 
                        }}
                      />
                      {hotel?.id === "511e8df9-672d-42b1-9711-d82beb7baf64" && <img style={{ height: 40, marginTop: 30 }} src="https://cdn-dev.vicomanager.com/assets/FLEXIPASS-SALTO.png" />}
                      {hotel?.id === "59f21e07-f8a4-46f0-b294-b74f03348a9f" && <img style={{ height: 40, marginTop: 30 }} src="https://cdn.vicomanager.com/assets/FLEXIPASS-SALTO.png" />}
                    </div>}
                    {unlockError && <div style={{ padding: 10, textAlign: "center", color: "red" }}>
                      <h6 className="mb-0" style={{ color: "red", fontWeight: "bold" }}>{smartLockName}</h6>
                      <p>{unlockError}</p>
                      <button style={{ cursor: "pointer", fontSize: 15, background: "white", color: "#888", border: "1px solid #DDD", borderRadius: 10, padding: "8px 25px" }} onClick={() => {
                        setUnlockError(null)
                        setOpening(null)
                        setOpened(null)
                        setSmartLockId(null)
                        setSmartLockName(null)
                      }}>Back</button>
                    </div>}
                  </div>
                </div>
              )}

            </>
          )}
        </>
      )}



      { /* ACCESOS PRINCIPALES */}
      {(!showSmartLockSection && hasMainSection) && (
        <div className="text-center p-4 mb-2" style={{
          background: "white",
          borderRadius: "8px",
          border: "1px solid #DDD",
          color: "#666"
        }}>

          <h4 className="d-flex justify-content-center align-items-center">{String(titleMainSection)?.toUpperCase()}</h4>
          {descriptionMainSection && (
            <p className="text-center mb-4">{descriptionMainSection}</p>
          )}

          <div className="pt-2">

            {isHostzArosa(hotel.id) && (
              <>
                <p className="mb-0">{getUnitGroupAccessTitle("status.access_information.main_entrance_code_and_skiroom_code")}</p>
                {/* <h6 className="font-weight-bold font-size-lg mb-0" dangerouslySetInnerHTML={{ __html: stripStyles(loadFromPlaceholder('MAIN-ACCESS-INFO', roomCode, language, placeholders)) }}></h6> */}
                {checkIn?.smartLockPinCode && <>
                  <h6 className="font-weight-semibold font-size-md mb-0">{checkIn.smartLockPinCode}#</h6>
                  {/* <span className="d-block" style={{ borderRadius: 8, marginLeft: 40, marginRight: 40, padding: 8, backgroundColor: '#E0E0E0', marginBottom: 10, fontStyle: 'italic', fontSize: 14 }}>
                        {T('smartlocks.pin-code-arosa-explanation')}
                      </span> */}
                </>}
                {!checkIn?.smartLockPinCode && <h6 className="font-weight-semibold font-size-md mb-0" dangerouslySetInnerHTML={{ __html: stripStyles(unitGroupBackupAccessCode) + '#' }}></h6>}
                {showTextMain && (
                  <p className="mt-4 mb-0">{processHtml(showTextMain)}</p>
                )}
              </>
            )}
            {(isHostzSigma(hotel.id) || isHostzSwissstay(hotel.id)) && (
              <>
                <p className="mb-0">{getUnitGroupAccessTitle(
                  isHostzSwissstay(hotel.id)
                    ? "status.access_information.main_entrance_code_swissstay"
                    : "status.access_information.main_entrance_code_sigma"
                )}</p>
                {checkIn?.smartLockPinCode && <>
                  <h6 className="font-weight-semibold font-size-md mb-0">{checkIn.smartLockPinCode}↵</h6>
                </>}
                {!checkIn?.smartLockPinCode && <h6 className="font-weight-semibold font-size-md mb-0" dangerouslySetInnerHTML={{ __html: stripStyles(unitGroupBackupAccessCode) + '↵' }}></h6>}
                {showTextMain && (
                  <p className="mt-4 mb-0">{processHtml(showTextMain)}</p>
                )}
              </>
            )}

            {isHostzCozy(hotel.id) && (
              <>
                <p className="mb-0">{getUnitGroupAccessTitle("status.access_code_one")}</p>
                {/* <h6 className="font-weight-bold font-size-lg mb-0" dangerouslySetInnerHTML={{ __html: stripStyles(loadFromPlaceholder('MAIN-ACCESS-INFO', roomCode, language, placeholders)) }}></h6> */}
                <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(unitGroupBackupAccessCode) }}></h6>
              </>
            )}
            {isHostzSteiners(hotel.id) && (
              <>
                {mainEntranceLockbox && mainEntranceLockbox != '--' && <>
                  <p className="mb-0">{getUnitGroupAccessTitle("status.access_code_one")}</p>
                  <h6 className="font-weight-semibold font-size-md">{mainEntranceLockbox}</h6>
                </>}
              </>
            )}

          </div>
        </div>
      )}

      {isHostzSigma(hotel.id) && (!showCoverBefore && !showCoverAfter && !showCoverMain) && (
        <div className="text-center p-2 mb-2" style={{
          background: "white",
          borderRadius: "8px",
          border: "1px solid #DDD",
          color: "#666"
        }}>
          <div className="d-flex flex-column justify-content-center items-align-center text-center">
            <p className="mb-0">{T("hotel.address")}</p>
            <h6 className="font-weight-semibold font-size-md mb-1" dangerouslySetInnerHTML={{ __html: stripStyles(hotelAddress(null, 'desc', language, placeholders)) }}></h6>
          </div>
        </div>
      )}

      {!showCoverKeys && isSimplex(hotel.id) && unitMainEntrance && unitMainEntrance != '--' && (
        <div className="text-center p-4 mb-2" style={{
          background: "white",
          borderRadius: "8px",
          border: "1px solid #DDD",
          color: "#666"
        }}>
          <h4 className="d-flex justify-content-center align-items-center">{String(T("status.access_information.main_entrance")).toUpperCase()}</h4>
          <h6 className="font-weight-semibold font-size-md pt-2 mb-1" dangerouslySetInnerHTML={{ __html: stripStyles(unitMainEntrance) }}></h6>
        </div>
      )}


      {/* KalmStay: entrada principal por placeholder UNITGROUP-ENTRANCE (solo si tiene valor) */}
      {isKalmStay(hotel.id) && unitGroupEntrance && unitGroupEntrance !== '--' && (
        <div className="text-center p-4 mb-2" style={{
          background: "white",
          borderRadius: "8px",
          border: "1px solid #DDD",
          color: "#666"
        }}>
          <h4 className="d-flex justify-content-center align-items-center">{String(T("status.access_information.main_entrance")).toUpperCase()}</h4>
          <h6 className="font-weight-semibold font-size-md pt-2 mb-1" dangerouslySetInnerHTML={{ __html: stripStyles(unitGroupEntrance) }}></h6>
        </div>
      )}

      { /* ACCESOS PARTICULARES */}
      {hasKeySection && (
        <div className="text-center p-4 mb-2" style={{
          background: "white",
          borderRadius: "8px",
          border: "1px solid #DDD",
          color: "#666"
        }}>

          <h4 className="d-flex justify-content-center align-items-center">{String(titleKeySection)?.toUpperCase()}</h4>
          {showCoverKeys && (
            <div>{showCoverKeys}</div>
          )}

          {!showCoverKeys && (
            <>

              {descriptionKeySection && (
                <p className="text-center mb-4">{descriptionKeySection}</p>
              )}


              <div className="pt-2">
                {isSimplex(hotel.id) && (
                  <>
                    {showRoomData(true)}
                    <p className="mb-0 mt-2">{T("status.access_code_one")}</p>
                    {/* <h6 className="font-weight-semibold font-size-md mb-1" dangerouslySetInnerHTML={{ __html: stripStyles(loadFromPlaceholder('UNIT-ACCESS-CODE', (Room?.RoomCategory?.code || RoomCategory?.code) + "--" + Room?.code, language, placeholders)) }}></h6> */}
                    <h6 className="font-weight-semibold font-size-md mb-1" dangerouslySetInnerHTML={{ __html: stripStyles(getPlaceholderValueFromCheckin('UNIT-ACCESS-CODE')) }}></h6>

                    {unitAccessIntructions && unitAccessIntructions != '--' && (
                      <>
                        <p className="mb-0 mt-2">{T("status.access_instructions")}</p>
                        <h6 className="font-weight-semibold font-size-md mb-1" dangerouslySetInnerHTML={{ __html: stripStyles(unitAccessIntructions) }}></h6>

                      </>
                    )
                    }
                  </>
                )}

                {isHostzBecozy(hotel.id) && (
                  <>
                    <p className="mb-0">{T("status.access_code.main_door")}</p>
                    <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(unitGroupBackupAccessCode) }}></h6>
                    <p className="mb-0">{T("status.access_code.room_door")}</p>
                    <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(isPlaceholderValid('UNIT-ROOM-ACCESS-INFO') ? getPlaceholderValueFromCheckin('UNIT-ROOM-ACCESS-INFO') : getPlaceholderValueFromCheckin('ROOM-ACCESS-INFO')) }}></h6>
                    {showRoomData()}
                    <br></br>
                    <p className="mb-0">{T("status.access_code.hostz-becozy")}</p>
                  </>
                )}

                {isHostzBlackSummit(hotel.id) && (
                  <>
                    <p className="mb-4">
                      <span dangerouslySetInnerHTML={{ __html: stripStyles(isPlaceholderValid('UNIT-ROOM-ACCESS-INFO') ? getPlaceholderValueFromCheckin('UNIT-ROOM-ACCESS-INFO') : getPlaceholderValueFromCheckin('ROOM-ACCESS-INFO')) }}></span>
                    </p>
                    {showRoomData(true)}
                  </>
                )}

                {isHostzSanCarlo(hotel.id) && (
                  <>
                    <p className="mb-0">{T("status.access_code.main_door")}</p>
                    <h6 className="font-weight-semibold font-size-md">555666*</h6>
                    <p className="mb-0">{T("status.access_code.key_collection_code")}</p>
                    <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(isPlaceholderValid('UNIT-ROOM-ACCESS-INFO') ? getPlaceholderValueFromCheckin('UNIT-ROOM-ACCESS-INFO') : getPlaceholderValueFromCheckin('ROOM-ACCESS-INFO')) }}></h6>
                    <p className="mb-0">{T("status.access_code.key_dropoff_code")}</p>
                    <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(isPlaceholderValid('UNIT-ROOM-DROPOFF-CODE') ? getPlaceholderValueFromCheckin('UNIT-ROOM-DROPOFF-CODE') : getPlaceholderValueFromCheckin('ROOM-DROPOFF-CODE')) }}></h6>
                    {showRoomData()}
                  </>
                )}

                {(isHostzArosa(hotel.id) || isHostzSigma(hotel.id)) && (
                  <>
                    {showRoomData()}
                    <p className="mb-0">{T("status.access_information.room_keybox_code_arosa")}</p>
                    {/* <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(loadFromPlaceholder('ROOM-ACCESS-INFO', roomCode, language, placeholders)) }}></h6> */}
                    <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(isPlaceholderValid('UNIT-ROOM-ACCESS-INFO') ? getPlaceholderValueFromCheckin('UNIT-ROOM-ACCESS-INFO') : getPlaceholderValueFromCheckin('ROOM-ACCESS-INFO')) }}></h6>
                    <p className="mb-0">{T("status.access_information.please_use_main_key")}</p>
                  </>
                )}
                {isHostzSwissstay(hotel.id) && (
                  <>
                    {/* {showRoomData(true, stripStyles(loadFromPlaceholder('UNITGROUP-ADDRESS', Room?.RoomCategory?.code || RoomCategory?.code, language, placeholders)))} */}
                    {showRoomData(true, stripStyles(getPlaceholderValueFromCheckin('UNITGROUP-ADDRESS')))}
                    <p className="mb-0">{T("status.access_information.room_lock_box_code")}</p>
                    {/* <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(loadFromPlaceholder('UNIT-LOCKBOX-CODE', (Room?.RoomCategory?.code || RoomCategory?.code) + "--" + Room?.code, language, placeholders)) }}></h6> */}
                    <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(getPlaceholderValueFromCheckin('UNIT-LOCKBOX-CODE')) }}></h6>
                    <p className="mb-0">{T("status.access_information.room_lock_box_location")}</p>
                    {/* <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(loadFromPlaceholder('UNIT-LOCKBOX-LOCATION', (Room?.RoomCategory?.code || RoomCategory?.code) + "--" + Room?.code, language, placeholders)) }}></h6> */}
                    <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(getPlaceholderValueFromCheckin('UNIT-LOCKBOX-LOCATION')) }}></h6>

                  </>
                )}

                {isHostzMyde(hotel.id) && (
                  <>
                    {showRoomData(true)}
                    <p className="mb-0">{T("status.access_code.ladder_themyde")}</p>
                    <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(loadFromPlaceholder('PROPERTY-LADDER-CODE', "__MAIN__", language, placeholders)) }}></h6>
                  </>
                )}

                {(isHostzSteiners(hotel.id) || isHostzCozy(hotel.id)) && (
                  <>
                    {/* 
                    TODO:::: PIN CODE DESDE EL CHECKIN 
                    parche para stainers keynest ya que el smartlockpincode
                    en la pwa siempre se usaba cuando estas en instay
                    */}
                    {checkIn?.smartLockPinCode && <div>
                      <p className="mb-0">{T("status.access_code.key_card_collection_code")}</p>
                      <h6 className="font-weight-semibold font-size-md">{checkIn.smartLockPinCode}</h6>
                    </div>}
                    <p className="mb-0">{T("status.access_code.key_dropoff_code")}</p>
                    <h6 className="font-weight-semibold font-size-md" dangerouslySetInnerHTML={{ __html: stripStyles(isPlaceholderValid('UNIT-ROOM-DROPOFF-CODE') ? getPlaceholderValueFromCheckin('UNIT-ROOM-DROPOFF-CODE') : getPlaceholderValueFromCheckin('ROOM-DROPOFF-CODE')) }}></h6>
                    {showRoomData()}
                  </>
                )}

                {isKalmStay(hotel.id) && (
                  <>
                    {kalmStayPinCode && (
                      <>
                        <p className="mb-0">{T("status.access_code.pin_code")}</p>
                        <h6 className="font-weight-semibold font-size-md mb-2">{kalmStayPinCode}</h6>
                      </>
                    )}
                    {showRoomData()}
                  </>
                )}

              </div>
            </>
          )}

        </div>
      )}

      {/* SI NINGUNA TAPA ESTÁ ACTIVA => MUESTRO DIRECCIÓN DEL HOTEL */}
      {(!showCoverBefore && !showCoverAfter && !showCoverMain) && (
        <>
          {!isHostzSwissstay(hotel.id) && !isSimplex(hotel.id) && !isHostzSigma(hotel.id) && (
            <>
              <div className="text-center p-2" style={{
                background: "white",
                borderRadius: "8px",
                border: "1px solid #DDD",
                color: "#666"
              }}>
                <div className="d-flex flex-column justify-content-center items-align-center text-center">
                  <p className="mb-0">{T("hotel.address")}</p>
                  <h6 className="font-weight-semibold font-size-md mb-1" dangerouslySetInnerHTML={{ __html: stripStyles(hotelAddress(null, 'desc', language, placeholders)) }}></h6>
                </div>
              </div>
            </>
          )}

        </>
      )}

      {accessInfoUrl && (
        <div className="text-center p-4 mt-2 mb-2" style={{
          background: "#c2e6ff",
          borderRadius: "8px",
          border: "1px solid #8acdfc",
          color: "#666"
        }}>
          <ButtonVideoModal urlVideo={accessInfoUrl} textButton={isHostzMyde(hotel.id) ? T('status.access_information.link_modal_video_themyde') : T('status.access_information.link_modal_video')} titleModal={T('status.access_information.title_modal_video')} />
        </div>
      )}

      {hotel?.extra?.["access-info-notice"]?.[language] && (
        <div className="text-center p-2 mt-2" style={{
          background: "white",
          borderRadius: "8px",
          border: "1px solid #DDD",
          color: "#666",
          backgroundColor: "#ffffc6",
          fontSize: "126%",
          padding: "20px 30px !important"
        }}>
          <div className="d-flex flex-column justify-content-center items-align-left">
            <div dangerouslySetInnerHTML={{ __html: stripStyles(hotel?.extra?.["access-info-notice"]?.[language]) }}></div>
          </div>
        </div>
      )}
    </Screen>
  );
}

export default SmartLockWithAccess;